import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Box } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import IntervalSliderItem from './IntervalSliderItem'

export type IntervalSliderItem = {
  title: string
  description: string
  img: string
}

interface IntervalSliderProps {
  items: IntervalSliderItem[]
  tabTimeOutInSecond: number
  /** If true, when slider item is clicked, it will stay active until other item is clicked. Defaults to TRUE */
  stopTimerOnClick?: boolean
  classes?: {
    step?: string
    activeStep?: string
  }
}

function IntervalSlider({
  items,
  tabTimeOutInSecond,
  stopTimerOnClick = true,
  classes: additionalClasses = {},
}: IntervalSliderProps) {
  const { classes } = useStyles()
  const [activeItemIndex, setActiveIndex] = useState(0)
  const [isTimerRunning, setIsTimerRunning] = useState(true)
  const interval = useRef<ReturnType<typeof setInterval>>()

  const startInterval = useCallback(() => {
    interval.current = setInterval(() => {
      setActiveIndex((prevIndex) => {
        let index = prevIndex + 1
        if (index > items.length - 1) {
          index = 0
        }
        return index
      })
    }, tabTimeOutInSecond * 1000)
  }, [tabTimeOutInSecond, items])

  const handleClick = (index: number) => {
    clearInterval(interval.current)
    setActiveIndex(index)
    if (!stopTimerOnClick) {
      startInterval()
    } else {
      if (isTimerRunning) {
        setIsTimerRunning(false)
      }
    }
  }

  useEffect(() => {
    startInterval()
    return () => {
      clearInterval(interval.current)
    }
  }, [])

  const activeImage = items[activeItemIndex].img

  return (
    <Box className={classes.container}>
      <Box className={classes.left}>
        {items.map((item, index) => {
          const isActive = activeItemIndex === index
          return (
            <IntervalSliderItem
              key={`slider-item-${index}`}
              index={index}
              onClick={() => handleClick(index)}
              isActive={isActive}
              title={item.title}
              description={item.description}
              img={item.img}
              tabTimeOutInSecond={tabTimeOutInSecond}
              classes={additionalClasses}
              showTimer={isTimerRunning}
            />
          )
        })}
      </Box>
      <Box className={classes.right}>
        <Box>
          <img className={classes.image} src={activeImage} alt="" />
        </Box>
      </Box>
    </Box>
  )
}

export default IntervalSlider

const useStyles = makeStyles()((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
  },
  left: {
    display: 'flex',
    flex: 2,
    flexDirection: 'column',
    marginRight: 20,
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      marginRight: 0,
    },
  },
  right: {
    display: 'flex',
    flex: 3,
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      display: 'none',
    },
  },
  image: {
    width: '100%',
    height: 'auto',
  },
  imageMobile: {
    [theme.breakpoints.up(theme.breakpoints.values.sm)]: {
      display: 'none',
    },
  },
}))
